import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import LeadForm from '@/components/LeadForm';
import { IMAGES } from '@/data/siteData';
import { trackEvent } from '@/lib/tracking';

const POPUP_DELAY = 30000;

const LeadPopup = () => {
  const [open, setOpen] = useState(false);
  const [manualSource, setManualSource] = useState(null);

  useEffect(() => {
    if (sessionStorage.getItem('ppv_popup_done')) return;
    const landedAt = Number(localStorage.getItem('ppv_landed_at') || 0);
    const now = Date.now();
    if (!landedAt) localStorage.setItem('ppv_landed_at', String(now));
    const elapsed = landedAt ? now - landedAt : 0;
    const remaining = Math.max(POPUP_DELAY - elapsed, 0);
    const t = setTimeout(() => {
      if (!sessionStorage.getItem('ppv_popup_done')) {
        setOpen(true);
        trackEvent('lead_popup_open', { trigger: 'timer' });
      }
    }, remaining);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setManualSource(e.detail?.source === 'site_visit' ? 'site_visit' : 'enquiry');
      setOpen(true);
      trackEvent('lead_popup_open', { trigger: 'manual' });
    };
    window.addEventListener('open-enquiry', handler);
    return () => window.removeEventListener('open-enquiry', handler);
  }, []);

  const close = () => {
    setOpen(false);
    sessionStorage.setItem('ppv_popup_done', '1');
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="lead-popup"
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="lead-popup"
          role="dialog"
          aria-modal="true"
          aria-label="Provident Palm Vista enquiry"
        >
          <div className="absolute inset-0 bg-palm/70 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-3xl bg-ivory rounded-3xl overflow-hidden shadow-2xl grid md:grid-cols-2 max-h-[92vh] overflow-y-auto"
          >
            <button onClick={close} data-testid="lead-popup-close" aria-label="Close popup"
              className="absolute top-3 right-3 z-10 h-9 w-9 rounded-full bg-palm/80 text-ivory flex items-center justify-center hover:bg-palm transition-colors">
              <X className="h-4 w-4" />
            </button>
            <div className="relative hidden md:block">
              <img src={IMAGES.poolLifestyle} alt="Poolside lifestyle at Provident Palm Vista" className="absolute inset-0 h-full w-full object-cover" loading="lazy" width={851} height={1193} />
              <div className="absolute inset-0 bg-gradient-to-t from-palm/85 via-palm/25 to-transparent" />
              <div className="absolute bottom-0 p-7">
                <p className="text-gold text-[11px] font-semibold tracking-[0.25em] uppercase">Kalyan–Shilphata Road</p>
                <p className="font-display text-3xl text-ivory font-medium mt-2 leading-tight">16 acres. 70% open.<br />60+ amenities.</p>
              </div>
            </div>
            <div className="p-6 sm:p-8">
              <p className="text-[11px] font-semibold tracking-[0.25em] uppercase text-gold">
                {manualSource === 'site_visit' ? 'Book Your Site Visit' : 'Limited Period Offers'}
              </p>
              <h3 className="font-display text-2xl sm:text-3xl font-medium text-palm mt-2 leading-tight">
                {manualSource === 'site_visit' ? 'Experience Provident Palm Vista in person' : 'Interested in Provident Palm Vista?'}
              </h3>
              <p className="text-sm text-muted-foreground mt-2 mb-5 leading-relaxed">
                Get the latest price, floor plans, availability and project details.
              </p>
              <LeadForm source={manualSource === 'site_visit' ? 'site_visit' : manualSource || 'popup'} testidPrefix="lead-popup-form" compact />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LeadPopup;
